/**
 * ContractorWorkspace — post-award contractor view.
 *
 * Public route: /contractor-workspace?email=<email>&project_id=<id>
 *               (or ?token=<contractor_portal_token>&project_id=<id>)
 *
 * Fetches everything from a single `/api/contractor-portal/:token/full`
 * endpoint and renders:
 *   - Their active phase with progress bar + action buttons
 *   - Adjacent phases (previous · next)
 *   - Whole-project read-only timeline
 *   - Tabs: Messages · Change Orders · Daily Reports
 *
 * Mirrors the mockup at /award-cascade-preview.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircle2, Camera, FileText, MessageSquare, Send, Plus, Eye,
  ChevronRight, AlertTriangle, RefreshCw, ArrowDown, FileSignature, Download,
} from 'lucide-react';
import ScheduleActions from '@/components/ScheduleActions';
import SignContractDialog from '@/components/SignContractDialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const BLUE = '#2563EB';
const BLUE_LIGHT = '#3B82F6';
const BLUE_DARK = '#1D4ED8';

const fmtMoney = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return iso; }
};
const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
};

const Pill = ({ tone, children }) => {
  const styles = {
    blue:    'bg-blue-100 text-blue-700 border-blue-200',
    emerald: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber:   'bg-amber-100 text-amber-700 border-amber-200',
    slate:   'bg-slate-100 text-slate-700 border-slate-200',
    red:     'bg-red-100 text-red-700 border-red-200',
  }[tone] || 'bg-slate-100 text-slate-700 border-slate-200';
  return <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${styles}`}>{children}</span>;
};

// --------- Messages tab ----------
const MessagesTab = ({ data, refresh }) => {
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const send = async () => {
    if (!draft.trim()) return;
    setSending(true);
    try {
      await api.post('/contractor-messages', {
        project_id: data.project.id,
        contractor_id: data.contractor.id,
        phase_id: data.my_phase?.id,
        direction: 'inbound',
        sender_name: data.contractor.contact_name || data.contractor.name,
        body: draft.trim(),
      });
      setDraft('');
      await refresh();
    } catch (e) { toast.error('Send failed'); }
    finally { setSending(false); }
  };
  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-blue-600" />Messages</p>
        <Pill tone="blue">Live thread</Pill>
      </div>
      <div className="px-4 py-3 space-y-3 max-h-96 overflow-y-auto bg-slate-50">
        {(data.messages || []).length === 0 && (
          <p className="text-xs text-slate-500 text-center py-6">No messages yet. Say hi.</p>
        )}
        {(data.messages || []).map((m) => {
          const mine = m.direction === 'inbound';
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-md rounded-lg px-3 py-2 text-xs ${mine ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-white border border-slate-200 rounded-tl-sm text-slate-700'}`}>
                <p className="whitespace-pre-wrap">{m.body}</p>
                <p className={`text-[9px] mt-1 ${mine ? 'opacity-70' : 'text-slate-500'}`}>{m.sender_name} · {fmtTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="px-3 py-2 border-t border-slate-200 flex items-center gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
          placeholder="Reply…"
          className="flex-1 h-8 text-xs"
          data-testid="message-draft"
        />
        <Button size="sm" onClick={send} disabled={sending || !draft.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="send-message">
          <Send className="w-3 h-3 mr-1" />Send
        </Button>
      </div>
    </Card>
  );
};

// --------- Change Orders tab ----------
const ChangeOrdersTab = ({ data, refresh }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', amount_delta: 0, days_delta: 0 });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSubmitting(true);
    try {
      await api.post('/change-orders', {
        project_id: data.project.id,
        contractor_id: data.contractor.id,
        phase_id: data.my_phase?.id,
        ...form,
      });
      setOpen(false);
      setForm({ title: '', description: '', amount_delta: 0, days_delta: 0 });
      toast.success('Change order submitted');
      await refresh();
    } catch (e) { toast.error('Submit failed'); }
    finally { setSubmitting(false); }
  };
  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4 text-amber-600" />Change Orders</p>
        <Button size="sm" onClick={() => setOpen(!open)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-co-btn">
          <Plus className="w-3 h-3 mr-1" />New CO
        </Button>
      </div>
      {open && (
        <div className="px-4 py-3 border-b border-slate-200 bg-blue-50 space-y-2">
          <Input placeholder="Title (e.g., Truss redesign)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="h-8 text-xs" data-testid="co-title" />
          <Textarea placeholder="Why is this needed?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="text-xs min-h-[60px]" data-testid="co-desc" />
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] text-slate-600 font-medium">Cost change ($)</label><Input type="number" value={form.amount_delta} onChange={(e) => setForm({ ...form, amount_delta: Number(e.target.value) })} className="h-8 text-xs font-mono" data-testid="co-amount" /></div>
            <div><label className="text-[10px] text-slate-600 font-medium">Schedule (days)</label><Input type="number" value={form.days_delta} onChange={(e) => setForm({ ...form, days_delta: Number(e.target.value) })} className="h-8 text-xs font-mono" data-testid="co-days" /></div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="co-submit">{submitting ? 'Submitting…' : 'Submit CO'}</Button>
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}
      <div className="px-4 py-3 space-y-2 max-h-96 overflow-y-auto">
        {(data.change_orders || []).length === 0 && (
          <p className="text-xs text-slate-500 text-center py-6">No change orders submitted yet.</p>
        )}
        {(data.change_orders || []).map((co) => {
          const tone = co.status === 'approved' ? 'emerald' : co.status === 'rejected' ? 'red' : co.status === 'counter' ? 'amber' : 'blue';
          return (
            <div key={co.id} className="border border-slate-200 rounded p-3" data-testid={`co-${co.id}`}>
              <div className="flex items-center justify-between mb-1">
                <p className="font-semibold text-sm text-slate-900">{co.title}</p>
                <Pill tone={tone}>{co.status}</Pill>
              </div>
              <p className="text-[11px] text-slate-600 mb-1.5">{co.description}</p>
              <div className="flex gap-3 text-[11px] text-slate-700">
                <span><strong className="font-mono">{co.amount_delta >= 0 ? '+' : ''}{fmtMoney(co.amount_delta)}</strong></span>
                <span><strong>{co.days_delta >= 0 ? '+' : ''}{co.days_delta}d</strong></span>
                <span className="text-slate-500">{fmtTime(co.created_at)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// --------- Daily Reports tab ----------
const DailyReportsTab = ({ data, refresh }) => {
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ report_date: today, crew_size: '', hours_worked: '', weather: '', work_completed: '', blockers: '' });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!form.work_completed.trim()) { toast.error('Describe what was done today'); return; }
    setSubmitting(true);
    try {
      await api.post('/daily-reports', {
        project_id: data.project.id,
        contractor_id: data.contractor.id,
        phase_id: data.my_phase?.id,
        report_date: form.report_date,
        crew_size: form.crew_size ? Number(form.crew_size) : null,
        hours_worked: form.hours_worked ? Number(form.hours_worked) : null,
        weather: form.weather || null,
        work_completed: form.work_completed,
        blockers: form.blockers || null,
      });
      setOpen(false);
      setForm({ report_date: today, crew_size: '', hours_worked: '', weather: '', work_completed: '', blockers: '' });
      toast.success('Daily report submitted');
      await refresh();
    } catch (e) { toast.error('Submit failed'); }
    finally { setSubmitting(false); }
  };
  return (
    <Card className="p-0 overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Camera className="w-4 h-4 text-emerald-600" />Daily Reports</p>
        <Button size="sm" onClick={() => setOpen(!open)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-dr-btn">
          <Plus className="w-3 h-3 mr-1" />Submit today
        </Button>
      </div>
      {open && (
        <div className="px-4 py-3 border-b border-slate-200 bg-emerald-50 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] text-slate-600 font-medium">Date</label><Input type="date" value={form.report_date} onChange={(e) => setForm({ ...form, report_date: e.target.value })} className="h-8 text-xs" /></div>
            <div><label className="text-[10px] text-slate-600 font-medium">Weather</label><Input placeholder="38°F clear" value={form.weather} onChange={(e) => setForm({ ...form, weather: e.target.value })} className="h-8 text-xs" /></div>
            <div><label className="text-[10px] text-slate-600 font-medium">Crew size</label><Input type="number" value={form.crew_size} onChange={(e) => setForm({ ...form, crew_size: e.target.value })} className="h-8 text-xs font-mono" /></div>
            <div><label className="text-[10px] text-slate-600 font-medium">Hours worked</label><Input type="number" step="0.5" value={form.hours_worked} onChange={(e) => setForm({ ...form, hours_worked: e.target.value })} className="h-8 text-xs font-mono" /></div>
          </div>
          <div><label className="text-[10px] text-slate-600 font-medium">Work completed</label><Textarea value={form.work_completed} onChange={(e) => setForm({ ...form, work_completed: e.target.value })} placeholder="What did the crew finish today?" className="text-xs min-h-[60px]" data-testid="dr-work" /></div>
          <div><label className="text-[10px] text-slate-600 font-medium">Blockers (optional)</label><Textarea value={form.blockers} onChange={(e) => setForm({ ...form, blockers: e.target.value })} placeholder="Anything holding things up?" className="text-xs min-h-[44px]" /></div>
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700" data-testid="dr-submit">{submitting ? 'Submitting…' : 'Submit report'}</Button>
            <Button size="sm" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          </div>
        </div>
      )}
      <div className="px-4 py-3 space-y-2 max-h-96 overflow-y-auto">
        {(data.daily_reports || []).length === 0 && (
          <p className="text-xs text-slate-500 text-center py-6">No daily reports yet. Submit today's update above.</p>
        )}
        {(data.daily_reports || []).map((r) => (
          <div key={r.id} className="border border-slate-200 rounded p-3 text-xs">
            <div className="flex items-center justify-between mb-1.5">
              <p className="font-semibold text-slate-900">{fmtDate(r.report_date)}</p>
              <span className="text-[10px] text-slate-500">Crew {r.crew_size ?? '—'} · {r.hours_worked ?? '—'}h · {r.weather || '—'}</span>
            </div>
            <p className="text-slate-700"><strong>Work:</strong> {r.work_completed}</p>
            {r.blockers && <p className="text-amber-700 mt-1"><strong>Blockers:</strong> {r.blockers}</p>}
          </div>
        ))}
      </div>
    </Card>
  );
};

// =====================================================================
export default function ContractorWorkspace() {
  const [params] = useSearchParams();
  const token = params.get('token') || params.get('email');
  const projectId = params.get('project_id');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('messages');
  const [signContract, setSignContract] = useState(null); // contract object being signed

  const load = async () => {
    if (!token || !projectId) {
      setError('Missing token or project_id in URL');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get(`/contractor-portal/${encodeURIComponent(token)}/full?project_id=${projectId}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load your workspace');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token, projectId]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">Loading your workspace…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <Card className="p-8 max-w-md text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold mb-2">Can't load your workspace</h2>
        <p className="text-sm text-slate-600">{error}</p>
        <p className="text-xs text-slate-500 mt-3">Check that your portal link includes both <code>token</code> (or <code>email</code>) and <code>project_id</code>.</p>
      </Card>
    </div>
  );
  if (!data) return null;

  const me = data.contractor;
  const ph = data.my_phase;
  const proj = data.project;
  const progress = ph?.progress_percent || ph?.completion_percent || 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contractor-workspace">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${BLUE_LIGHT}, ${BLUE_DARK})` }}>
              <span className="text-white font-bold text-sm">D</span>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Contractor Portal</p>
              <h1 className="text-white text-base font-semibold">{me.name}</h1>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-400 uppercase">{proj?.name}</p>
            <p className="text-xs text-slate-300">{proj?.address || ''}</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-5 space-y-5">
        {/* Active scope */}
        {ph ? (
          <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4" data-testid="active-scope">
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-0.5">Your Active Scope</p>
                <h2 className="text-2xl font-bold text-slate-900">{ph.name || ph.phase_name}</h2>
                <p className="text-xs text-slate-600 mt-1">
                  {fmtDate(ph.start_date)} → {fmtDate(ph.end_date || ph.expected_completion_date)} ·
                  <span className="ml-1 font-mono">{progress}% complete</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">Status</p>
                <p className="text-lg font-bold text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />On schedule</p>
              </div>
            </div>
            <div className="w-full bg-white rounded-full h-2 overflow-hidden">
              <div className="h-full bg-blue-600 transition-all" style={{ width: `${Math.min(100, Math.max(0, progress))}%` }} />
            </div>
          </div>
        ) : (
          <Card className="p-4 bg-amber-50 border-amber-200 text-amber-900 text-sm flex gap-2 items-start">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <p>You haven't been assigned to a specific phase on this project yet. Once you are, your scope appears here.</p>
          </Card>
        )}

        {/* Schedule actions — keep dates accurate for the whole project */}
        {ph && (
          <ScheduleActions
            data={data}
            phase={ph}
            projectId={projectId || proj?.id}
            onUpdated={load}
          />
        )}

        {/* Adjacent phases */}
        {(data.adjacent?.previous || data.adjacent?.next) && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Adjacent phases — for context</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {data.adjacent.previous ? (
                <Card className="p-3 bg-slate-50" data-testid="prev-phase">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-slate-700">← {data.adjacent.previous.name || data.adjacent.previous.phase_name}</p>
                    <Pill tone={data.adjacent.previous.status === 'complete' ? 'emerald' : 'slate'}>{data.adjacent.previous.status || '—'}</Pill>
                  </div>
                  <p className="text-[11px] text-slate-500">{fmtDate(data.adjacent.previous.start_date)} → {fmtDate(data.adjacent.previous.end_date)}</p>
                </Card>
              ) : <div />}
              {data.adjacent.next ? (
                <Card className="p-3 bg-slate-50" data-testid="next-phase">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-slate-700">{data.adjacent.next.name || data.adjacent.next.phase_name} →</p>
                    <Pill tone="blue">next</Pill>
                  </div>
                  <p className="text-[11px] text-slate-500">{fmtDate(data.adjacent.next.start_date)} → {fmtDate(data.adjacent.next.end_date)}</p>
                </Card>
              ) : <div />}
            </div>
          </div>
        )}

        {/* Whole project read-only */}
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5"><Eye className="w-3 h-3" />Project timeline — read-only</p>
          <Card className="p-0 overflow-hidden">
            {(proj?.phases || []).map((p, idx, arr) => (
              <div
                key={p.id || idx}
                className={`flex items-center gap-3 px-3 py-2 ${idx < arr.length - 1 ? 'border-b border-slate-100' : ''} ${p.is_mine ? 'bg-blue-50' : ''}`}
                data-testid={`project-phase-${idx}`}
              >
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${
                  p.status === 'complete' ? 'bg-emerald-100 text-emerald-700' :
                  p.status === 'active' || p.is_mine ? 'bg-blue-600 text-white' :
                  p.status === 'in_progress' ? 'bg-blue-600 text-white' :
                  'bg-slate-100 text-slate-500'
                }`}>{p.num}</span>
                <div className="flex-1 min-w-0">
                  <p className={`text-xs ${p.is_mine ? 'font-bold text-slate-900' : 'font-medium text-slate-700'} truncate`}>
                    {p.name} {p.is_mine && <span className="text-blue-600">(you)</span>}
                  </p>
                  <p className="text-[10px] text-slate-500">{fmtDate(p.start_date)} – {fmtDate(p.end_date)}</p>
                </div>
                <span className="text-[10px] font-mono text-slate-500 w-12 text-right">{p.progress_percent || 0}%</span>
              </div>
            ))}
            {(!proj?.phases || proj.phases.length === 0) && (
              <p className="px-4 py-6 text-xs text-slate-500 text-center">No phases set up on this project yet.</p>
            )}
          </Card>
        </div>

        {/* Contracts (Tier 1 PDF + Tier 2 E-Signature) */}
        {(data.contracts && data.contracts.length > 0) && (
          <div data-testid="contracts-section">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
              <FileSignature className="w-3 h-3" />Your contracts
            </p>
            <Card className="p-0 overflow-hidden">
              {data.contracts.map((c, idx, arr) => {
                const apiBase = process.env.REACT_APP_BACKEND_URL || '';
                const url = `${apiBase}/api/contracts/${c.id}/pdf?contractor_token=${encodeURIComponent(token || '')}`;
                const amount = c.contract_amount ? fmtMoney(c.contract_amount) : 'TBD';
                const status = c.status || 'issued';
                const badge = (() => {
                  if (status === 'fully_executed') return { label: 'Fully executed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
                  if (status === 'signed_by_contractor') return { label: 'Awaiting countersign', cls: 'bg-amber-100 text-amber-800 border-amber-200' };
                  return { label: 'Awaiting your signature', cls: 'bg-blue-100 text-blue-800 border-blue-200' };
                })();
                return (
                  <div
                    key={c.id}
                    className={`flex items-center gap-3 px-4 py-3 ${idx < arr.length - 1 ? 'border-b border-slate-100' : ''}`}
                    data-testid={`contract-row-${idx}`}
                  >
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-blue-50 flex-shrink-0">
                      <FileSignature className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xs font-semibold text-slate-900 truncate">
                          Contract Summary · {c.project_name}
                        </p>
                        <span
                          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${badge.cls}`}
                          data-testid={`contract-status-${idx}`}
                        >
                          {badge.label}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500">
                        {amount}{c.start_date && ` · starts ${fmtDate(c.start_date)}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {status === 'issued' && (
                        <Button
                          size="sm"
                          onClick={() => setSignContract(c)}
                          data-testid={`contract-sign-${idx}`}
                          className="text-xs"
                        >
                          <FileSignature className="w-3 h-3 mr-1" />
                          Sign
                        </Button>
                      )}
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 px-3 py-1.5 rounded-md bg-blue-50 hover:bg-blue-100 transition-colors"
                        data-testid={`contract-download-${idx}`}
                      >
                        <Download className="w-3 h-3" />
                        View PDF
                      </a>
                    </div>
                  </div>
                );
              })}
            </Card>
            <p className="text-[10px] text-slate-500 mt-2">
              E-signatures recorded with timestamp + IP · Owner countersigns after you sign
            </p>
          </div>
        )}

        <SignContractDialog
          open={!!signContract}
          contract={signContract}
          mode="contractor"
          contractorToken={token}
          defaultName={me?.contact_name || me?.name || ''}
          onClose={() => setSignContract(null)}
          onSigned={() => { setSignContract(null); load(); }}
        />

        {/* Tabbed surface: Messages / Change Orders / Daily Reports */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 max-w-md">
            <TabsTrigger value="messages" data-testid="tab-messages"><MessageSquare className="w-3.5 h-3.5 mr-1" />Messages</TabsTrigger>
            <TabsTrigger value="change-orders" data-testid="tab-change-orders"><FileText className="w-3.5 h-3.5 mr-1" />Change Orders</TabsTrigger>
            <TabsTrigger value="daily-reports" data-testid="tab-daily-reports"><Camera className="w-3.5 h-3.5 mr-1" />Daily Reports</TabsTrigger>
          </TabsList>
          <div className="mt-3">
            <TabsContent value="messages"><MessagesTab data={data} refresh={load} /></TabsContent>
            <TabsContent value="change-orders"><ChangeOrdersTab data={data} refresh={load} /></TabsContent>
            <TabsContent value="daily-reports"><DailyReportsTab data={data} refresh={load} /></TabsContent>
          </div>
        </Tabs>

        <div className="text-center pt-2">
          <button onClick={load} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </main>

      <footer className="text-center text-[10px] text-slate-500 py-6 border-t border-slate-200 mt-8">
        DrawReady · Contractor Workspace
      </footer>
    </div>
  );
}
